import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { allVideos } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const videoId = Number(id);
  const video: Video | undefined = allVideos.find(v => v.id === videoId);

  useEffect(() => {
    if (id && isNaN(videoId)) {
      navigate('/');
    }
  }, [id, videoId, navigate]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20 flex flex-col items-center justify-center">
          <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4"></i>
          <h1 className="text-xl font-bold text-foreground-800 mb-2">影视资源未找到</h1>
          <p className="text-foreground-500 mb-4">该影视资源可能已被移除或链接无效</p>
          <Link
            to="/"
            className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = allVideos
    .filter(v => v.categorySlug === video.categorySlug && v.id !== video.id)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: video.title,
    description: video.description,
    image: video.thumbnail,
    url: `https://www.91onlinevideo.com/article/${video.id}`,
    dateCreated: video.releaseDate,
    director: {
      '@type': 'Person',
      name: video.director,
    },
    actor: video.actors.map(name => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: video.rating,
      bestRating: 10,
    },
    genre: video.tags,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: video.category, href: `/category/${video.categorySlug}` },
            { label: video.title },
          ]} />

          <article className="max-w-5xl mx-auto">
            <div className="relative aspect-video rounded-xl overflow-hidden bg-background-900 mb-6">
              <img
                src={video.thumbnail}
                alt={video.title}
                title={`${video.title} - 91在线视频`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer group">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-500 rounded-full flex items-center justify-center group-hover:bg-primary-600 transition-all group-hover:scale-110">
                  <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 bg-black/75 text-white text-sm px-3 py-1 rounded-lg">
                {video.duration}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
                  {video.title}
                </h1>

                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="bg-accent-100 text-accent-800 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-star-fill"></i>
                    {video.rating} 分
                  </span>
                  <span className="text-sm text-foreground-600">{video.year}年</span>
                  <span className="text-sm text-foreground-600">{video.duration}</span>
                  <span className="text-sm text-foreground-600">{video.region}</span>
                  <span className="bg-secondary-100 text-secondary-800 text-xs px-2.5 py-1 rounded-full">
                    {video.type}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {video.tags.map(tag => (
                    <span key={tag} className="text-xs px-3 py-1 bg-background-100 text-foreground-600 rounded-full border border-background-200">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="prose prose-sm max-w-none">
                  <h2 className="text-base font-semibold text-foreground-800 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{video.description}</p>
                </div>

                <div className="mt-6 space-y-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500 w-16 flex-shrink-0">导演：</span>
                    <span className="text-foreground-800">{video.director}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 w-16 flex-shrink-0">主演：</span>
                    <span className="text-foreground-800">{video.actors.join('、')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500 w-16 flex-shrink-0">类型：</span>
                    <span className="text-foreground-800">{video.type}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500 w-16 flex-shrink-0">地区：</span>
                    <span className="text-foreground-800">{video.region}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500 w-16 flex-shrink-0">上映：</span>
                    <span className="text-foreground-800">{video.releaseDate}</span>
                  </div>
                  {video.episodes && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-foreground-500 w-16 flex-shrink-0">集数：</span>
                      <span className="text-foreground-800">共{video.episodes}集</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500 w-16 flex-shrink-0">播放：</span>
                    <span className="text-foreground-800">{(video.views / 10000).toFixed(0)}万次</span>
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-xl bg-background-100 p-5">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">
                    <i className="ri-movie-line text-primary-500 mr-1.5"></i>
                    相关推荐
                  </h3>
                  <div className="space-y-4">
                    {relatedVideos.map((rv) => (
                      <Link
                        key={rv.id}
                        to={`/article/${rv.id}`}
                        className="flex gap-3 group"
                      >
                        <div className="relative w-28 flex-shrink-0 aspect-video rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={rv.thumbnail}
                            alt={rv.title}
                            title={rv.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {rv.title}
                          </h4>
                          <div className="flex items-center gap-1.5 mt-1.5 text-xs text-foreground-500">
                            <span className="text-accent-600 flex items-center gap-0.5">
                              <i className="ri-star-fill text-xs"></i>{rv.rating}
                            </span>
                            <span>·</span>
                            <span>{rv.year}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div className="w-full px-4 md:px-6 py-10 mt-8 bg-background-50">
          <h2 className="text-lg font-bold font-heading text-foreground-900 mb-5 max-w-5xl mx-auto">
            <i className="ri-play-list-2-line text-primary-500 mr-2"></i>
            更多{video.category}推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {allVideos.filter(v => v.categorySlug === video.categorySlug && v.id !== video.id).slice(0, 5).map((rv) => (
              <Link key={rv.id} to={`/article/${rv.id}`} className="group block">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-2">
                  <img src={rv.thumbnail} alt={rv.title} title={rv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 bg-primary-500/90 rounded-full flex items-center justify-center">
                      <i className="ri-play-fill text-white text-sm ml-0.5"></i>
                    </div>
                  </div>
                </div>
                <h4 className="text-xs font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">{rv.title}</h4>
                <span className="text-xs text-foreground-500">{rv.year} · {rv.rating}分</span>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}